import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getIcon,
  SLRPrompt,
  SLRPromptLabels,
  SLRSpinner,
  SLRTable,
  TableColumn
} from '@slr/shared-library';
import {
  ADMINISTRATION_ORGANIZATIONS_PATH,
  ADMINISTRATION_USERS_PATH
} from 'configs';
import {
  useRemovePlatformAdminOffer,
  useSuspendPlatformAdminOffer,
  useUnsuspendPlatformAdminOffer
} from 'hooks/admin/usePlatformAdminOfferMutations';
import {
  PlatformAdminOfferAvailabilityFilter,
  PlatformAdminOfferSortOption,
  PlatformAdminOfferStatusFilter,
  PlatformAdminOfferTypeFilter,
  useSearchPlatformAdminOffers
} from 'hooks/admin/useSearchPlatformAdminOffers';
import { Empty } from 'pages/organizations';
import { FC, FormEvent, useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  InputGroup,
  Pagination,
  Row
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';
import { PlatformAdminOffer } from 'services/admin/platformAdminOffer.types';
import { globalNavigationConfig } from 'settings/global-navigation.settings';
import { openAdministrationViewWithSearch } from './administrationNavigation';
import { AdministrationOutletContext } from './AdministrationPage';
import { useAdministrationSearchUrlSync } from './useAdministrationSearchUrlSync';
import './AdministrationOffersPage.scss';

const PAGE_SIZE_OPTIONS = [1, 10, 25, 50] as const;

const TYPE_FILTER_OPTIONS: PlatformAdminOfferTypeFilter[] = [
  'all',
  'ROOM',
  'MATERIAL'
];

const STATUS_FILTER_OPTIONS: PlatformAdminOfferStatusFilter[] = [
  'all',
  'draft',
  'published'
];

const AVAILABILITY_FILTER_OPTIONS: PlatformAdminOfferAvailabilityFilter[] = [
  'active',
  'suspended',
  'removed',
  'all'
];

const SORT_OPTIONS: PlatformAdminOfferSortOption[] = [
  'name-asc',
  'name-desc',
  'publishedAt-desc',
  'publishedAt-asc'
];

const buildOfferDetailsUrl = (offerId: string) =>
  `${globalNavigationConfig.start}/booking/offers/${offerId}?platformAdminPreview=1`;

export const AdministrationOffersPage: FC = () => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'administrationOffersPage'
  });
  const navigate = useNavigate();
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
  const {
    offersFilters,
    setOffersFilters,
    setUsersFilters,
    setOrganizationsFilters
  } = useOutletContext<AdministrationOutletContext>();
  const {
    searchInput,
    appliedSearch,
    typeFilter,
    statusFilter = 'published',
    availabilityFilter = 'all',
    sortOption = 'name-asc',
    page,
    pageSize
  } = offersFilters;
  const { commitSearchToUrl } = useAdministrationSearchUrlSync(setOffersFilters);

  const { data, isPending } = useSearchPlatformAdminOffers(
    appliedSearch,
    page,
    pageSize,
    typeFilter,
    statusFilter,
    availabilityFilter,
    sortOption
  );
  const suspendMutation = useSuspendPlatformAdminOffer();
  const unsuspendMutation = useUnsuspendPlatformAdminOffer();
  const removeMutation = useRemovePlatformAdminOffer();

  const [offerPendingSuspension, setOfferPendingSuspension] =
    useState<PlatformAdminOffer | null>(null);
  const [offerPendingUnsuspension, setOfferPendingUnsuspension] =
    useState<PlatformAdminOffer | null>(null);
  const [offerPendingRemoval, setOfferPendingRemoval] =
    useState<PlatformAdminOffer | null>(null);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionMessageToProvider, setSuspensionMessageToProvider] =
    useState('');
  const [removalReason, setRemovalReason] = useState('');
  const [removalMessageToProvider, setRemovalMessageToProvider] = useState('');

  const rows = useMemo(() => data?.content ?? [], [data]);

  const closeSuspensionPrompt = useCallback(() => {
    setOfferPendingSuspension(null);
    setSuspensionReason('');
    setSuspensionMessageToProvider('');
  }, []);

  const closeUnsuspensionPrompt = useCallback(() => {
    setOfferPendingUnsuspension(null);
  }, []);

  const closeRemovalPrompt = useCallback(() => {
    setOfferPendingRemoval(null);
    setRemovalReason('');
    setRemovalMessageToProvider('');
  }, []);

  const handleConfirmSuspension = useCallback(async () => {
    if (!offerPendingSuspension || !suspensionReason.trim()) {
      return;
    }
    try {
      await suspendMutation.mutateAsync({
        roomId: offerPendingSuspension.id,
        reason: suspensionReason.trim(),
        messageToProvider: suspensionMessageToProvider.trim() || undefined
      });
      closeSuspensionPrompt();
    } catch {
    }
  }, [
    closeSuspensionPrompt,
    offerPendingSuspension,
    suspendMutation,
    suspensionMessageToProvider,
    suspensionReason
  ]);

  const handleConfirmUnsuspension = useCallback(async () => {
    if (!offerPendingUnsuspension) {
      return;
    }
    try {
      await unsuspendMutation.mutateAsync({
        roomId: offerPendingUnsuspension.id
      });
      closeUnsuspensionPrompt();
    } catch {
    }
  }, [closeUnsuspensionPrompt, offerPendingUnsuspension, unsuspendMutation]);

  const handleConfirmRemoval = useCallback(async () => {
    if (!offerPendingRemoval || !removalReason.trim()) {
      return;
    }
    try {
      await removeMutation.mutateAsync({
        roomId: offerPendingRemoval.id,
        reason: removalReason.trim(),
        messageToProvider: removalMessageToProvider.trim() || undefined
      });
      closeRemovalPrompt();
    } catch {
    }
  }, [
    closeRemovalPrompt,
    offerPendingRemoval,
    removalMessageToProvider,
    removalReason,
    removeMutation
  ]);

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedSearch = searchInput.trim();
    setOffersFilters((current) => ({
      ...current,
      page: 0,
      appliedSearch: trimmedSearch
    }));
    commitSearchToUrl(trimmedSearch);
  };

  const handleTypeFilterChange = (
    nextTypeFilter: PlatformAdminOfferTypeFilter
  ) => {
    setOffersFilters((current) => ({
      ...current,
      page: 0,
      typeFilter: nextTypeFilter
    }));
  };

  const handleStatusFilterChange = (
    nextStatusFilter: PlatformAdminOfferStatusFilter
  ) => {
    setOffersFilters((current) => ({
      ...current,
      page: 0,
      statusFilter: nextStatusFilter
    }));
  };

  const handleAvailabilityFilterChange = (
    nextAvailabilityFilter: PlatformAdminOfferAvailabilityFilter
  ) => {
    setOffersFilters((current) => ({
      ...current,
      page: 0,
      availabilityFilter: nextAvailabilityFilter
    }));
  };

  const handleSortOptionChange = (
    nextSortOption: PlatformAdminOfferSortOption
  ) => {
    setOffersFilters((current) => ({
      ...current,
      page: 0,
      sortOption: nextSortOption
    }));
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setOffersFilters((current) => ({
      ...current,
      page: 0,
      pageSize: nextPageSize
    }));
  };

  const handleOpenProviderUser = (providerEmail: string) => {
    openAdministrationViewWithSearch(
      navigate,
      ADMINISTRATION_USERS_PATH,
      providerEmail,
      setUsersFilters,
      { roleFilter: 'all' }
    );
  };

  const handleOpenOrganization = (organizationName: string) => {
    openAdministrationViewWithSearch(
      navigate,
      ADMINISTRATION_ORGANIZATIONS_PATH,
      organizationName,
      setOrganizationsFilters
    );
  };

  const columns: TableColumn<PlatformAdminOffer>[] = useMemo(
    () => [
      {
        label: t('columns.type'),
        accessor: 'type',
        cellFn: ({ data: offer }) => {
          let offerTypeLabel = t('emptyValue');
          if (offer.type === 'MATERIAL') {
            offerTypeLabel = t('offerType.material');
          } else if (offer.type === 'ROOM') {
            offerTypeLabel = t('offerType.room');
          }
          return (
            <span className="text-nowrap">{offerTypeLabel}</span>
          );
        }
      },
      {
        label: t('columns.offer'),
        accessor: 'name',
        cellFn: ({ data: offer }) => {
          const offerName = offer.name?.trim() || t('untitledOffer');
          return (
            <a
              href={buildOfferDetailsUrl(offer.id)}
              target="_blank"
              rel="noreferrer"
              className="administration-offers-page__offer-name"
              title={offerName}
            >
              {offerName}
            </a>
          );
        }
      },
      {
        label: t('columns.status'),
        accessor: 'visible',
        cellFn: ({ data: offer }) => (
          <span className="text-nowrap d-inline-flex align-items-center gap-2">
            {offer.visible
              ? t('offerStatus.published')
              : t('offerStatus.draft')}
            {offer.deleted ? (
              <Badge bg="secondary" text="light" className="administration-offers-page__removed-badge">
                {t('offerStatus.removed')}
              </Badge>
            ) : null}
            {!offer.deleted && offer.temporarilySuspended ? (
              <Badge bg="light" text="dark" className="administration-offers-page__suspended-badge">
                {t('offerStatus.suspended')}
              </Badge>
            ) : null}
            {!offer.deleted &&
              offer.temporarilySuspended &&
              offer.suspensionReviewRequested ? (
              <Badge bg="warning" text="dark">
                {t('offerStatus.reviewRequested')}
              </Badge>
            ) : null}
          </span>
        )
      },
      {
        label: t('columns.publishedAt'),
        accessor: 'publishedAt',
        cellFn: ({ data: offer }) => {
          if (offer.publishedAt == null) {
            return (
              <span className="text-nowrap">{t('emptyValue')}</span>
            );
          }
          return (
            <span className="text-nowrap">
              {new Date(offer.publishedAt).toLocaleDateString(dateLocale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </span>
          );
        }
      },
      {
        label: t('columns.provider'),
        accessor: 'providerName',
        cellFn: ({ data: offer }) => {
          const providerName = offer.providerName?.trim();
          const providerEmail = offer.providerEmail?.trim();
          return (
            <div className="administration-offers-page__provider d-flex align-items-center gap-2">
              {providerEmail ? (
                <Button
                  type="button"
                  variant="link"
                  className="administration-cross-link p-0"
                  onClick={() => handleOpenProviderUser(providerEmail)}
                  aria-label={t('openProviderUser', { email: providerEmail })}
                  title={providerEmail}
                >
                  {providerName || providerEmail}
                </Button>
              ) : (
                <span>{providerName || t('emptyValue')}</span>
              )}
              {providerEmail ? (
                <Button
                  as="a"
                  href={`mailto:${providerEmail}`}
                  size="sm"
                  variant="outline-primary"
                  className="administration-offers-page__email-button"
                  aria-label={t('emailProvider', { email: providerEmail })}
                  title={providerEmail}
                >
                  <FontAwesomeIcon icon={getIcon('fas', 'envelope')} />
                </Button>
              ) : null}
            </div>
          );
        }
      },
      {
        label: t('columns.providerType'),
        accessor: 'providerAddressType',
        cellFn: ({ data: offer }) => {
          let providerTypeLabel = t('emptyValue');
          if (offer.providerAddressType === 'business') {
            providerTypeLabel = t('providerType.business');
          } else if (offer.providerAddressType === 'private') {
            providerTypeLabel = t('providerType.private');
          }
          return (
            <span className="text-nowrap">{providerTypeLabel}</span>
          );
        }
      },
      {
        label: t('columns.organization'),
        accessor: 'organizationName',
        cellFn: ({ data: offer }) => {
          const organizationName = offer.organizationName?.trim();
          if (!organizationName) {
            return t('emptyValue');
          }
          return (
            <Button
              type="button"
              variant="link"
              className="administration-cross-link"
              onClick={() => handleOpenOrganization(organizationName)}
              aria-label={t('openOrganization', { name: organizationName })}
              title={organizationName}
            >
              {organizationName}
            </Button>
          );
        }
      },
      {
        label: t('columns.bookings'),
        accessor: 'bookingCount',
        cellFn: ({ value }) => String(value ?? 0)
      },
      {
        label: t('columns.bookingRequests'),
        accessor: 'bookingRequestCount',
        cellFn: ({ value }) => String(value ?? 0)
      },
      {
        label: t('columns.actions'),
        accessor: 'id',
        cellFn: ({ data: offer }) => {
          if (offer.deleted) {
            return <span className="text-nowrap">{t('emptyValue')}</span>;
          }
          return (
            <DropdownButton
              as={ButtonGroup}
              size="sm"
              variant="outline-primary"
              title={t('actions.label')}
            >
              {offer.temporarilySuspended ? (
                <Dropdown.Item
                  onClick={() => setOfferPendingUnsuspension(offer)}
                  disabled={unsuspendMutation.isPending || removeMutation.isPending}
                >
                  {t('actions.unlock')}
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  onClick={() => setOfferPendingSuspension(offer)}
                  disabled={suspendMutation.isPending || removeMutation.isPending}
                >
                  {t('actions.lock')}
                </Dropdown.Item>
              )}
              <Dropdown.Item
                onClick={() => setOfferPendingRemoval(offer)}
                disabled={
                  removeMutation.isPending ||
                  suspendMutation.isPending ||
                  unsuspendMutation.isPending
                }
              >
                {t('actions.remove')}
              </Dropdown.Item>
            </DropdownButton>
          );
        }
      }
    ],
    [
      dateLocale,
      removeMutation.isPending,
      suspendMutation.isPending,
      t,
      unsuspendMutation.isPending
    ]
  );

  const totalPages = Math.max(data?.totalPages ?? 0, 1);
  const offerNameForPrompt =
    offerPendingSuspension?.name?.trim() ||
    offerPendingUnsuspension?.name?.trim() ||
    offerPendingRemoval?.name?.trim() ||
    t('untitledOffer');

  return (
    <div className="administration-offers-page">
      {offerPendingSuspension && (
        <SLRPrompt
          isLoading={suspendMutation.isPending}
          confirmButtonVariant="danger"
          labels={
            t('lockConfirmation', {
              returnObjects: true,
              name: offerNameForPrompt
            }) as SLRPromptLabels
          }
          textEditor={{
            isInvalid: !suspensionReason.trim(),
            editor: (
              <div className="mt-3 d-flex flex-column gap-3">
                <Form.Group controlId="offer-suspension-reason">
                  <Form.Label>{t('lockConfirmation.reasonLabel')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={suspensionReason}
                    onChange={(event) => setSuspensionReason(event.target.value)}
                    placeholder={t('lockConfirmation.reasonPlaceholder')}
                    required
                  />
                </Form.Group>
                <Form.Group controlId="offer-suspension-message">
                  <Form.Label>{t('lockConfirmation.messageLabel')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={suspensionMessageToProvider}
                    onChange={(event) =>
                      setSuspensionMessageToProvider(event.target.value)
                    }
                    placeholder={t('lockConfirmation.messagePlaceholder')}
                  />
                </Form.Group>
              </div>
            )
          }}
          onConfirm={() => {
            void handleConfirmSuspension();
          }}
          onCancel={closeSuspensionPrompt}
        />
      )}
      {offerPendingUnsuspension && (
        <SLRPrompt
          isLoading={unsuspendMutation.isPending}
          confirmButtonVariant="primary"
          labels={
            t('unlockConfirmation', {
              returnObjects: true,
              name: offerNameForPrompt
            }) as SLRPromptLabels
          }
          onConfirm={() => {
            void handleConfirmUnsuspension();
          }}
          onCancel={closeUnsuspensionPrompt}
        />
      )}
      {offerPendingRemoval && (
        <SLRPrompt
          isLoading={removeMutation.isPending}
          confirmButtonVariant="danger"
          labels={
            t('removeConfirmation', {
              returnObjects: true,
              name: offerNameForPrompt
            }) as SLRPromptLabels
          }
          textEditor={{
            isInvalid: !removalReason.trim(),
            editor: (
              <div className="mt-3 d-flex flex-column gap-3">
                <Form.Group controlId="offer-removal-reason">
                  <Form.Label>{t('removeConfirmation.reasonLabel')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={removalReason}
                    onChange={(event) => setRemovalReason(event.target.value)}
                    placeholder={t('removeConfirmation.reasonPlaceholder')}
                    required
                  />
                </Form.Group>
                <Form.Group controlId="offer-removal-message">
                  <Form.Label>{t('removeConfirmation.messageLabel')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={removalMessageToProvider}
                    onChange={(event) =>
                      setRemovalMessageToProvider(event.target.value)
                    }
                    placeholder={t('removeConfirmation.messagePlaceholder')}
                  />
                </Form.Group>
              </div>
            )
          }}
          onConfirm={() => {
            void handleConfirmRemoval();
          }}
          onCancel={closeRemovalPrompt}
        />
      )}
      <Row className="administration-toolbar align-items-center justify-content-center g-3 mb-4">
        <Col xs={12} sm={8} md={6} lg={4} className="administration-toolbar__search">
          <Form onSubmit={handleSearchSubmit}>
            <InputGroup>
              <Form.Control
                type="search"
                value={searchInput}
                onChange={(event) => {
                  const nextSearchInput = event.target.value;
                  setOffersFilters((current) => {
                    if (
                      nextSearchInput === '' &&
                      current.appliedSearch !== ''
                    ) {
                      commitSearchToUrl('');
                      return {
                        ...current,
                        searchInput: nextSearchInput,
                        appliedSearch: '',
                        page: 0
                      };
                    }
                    return {
                      ...current,
                      searchInput: nextSearchInput
                    };
                  });
                }}
                placeholder={t('searchPlaceholder')}
                aria-label={t('searchPlaceholder')}
              />
              <Button type="submit" variant="primary">
                {t('search')}
              </Button>
            </InputGroup>
          </Form>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-offers-type-filter"
            className="administration-offers-page__filter d-flex align-items-center gap-2"
          >
            <Form.Label className="mb-0 text-nowrap">
              {t('typeFilter.label')}
            </Form.Label>
            <Form.Select
              value={typeFilter}
              onChange={(event) =>
                handleTypeFilterChange(
                  event.target.value as PlatformAdminOfferTypeFilter
                )
              }
            >
              {TYPE_FILTER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`typeFilter.${option}`)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-offers-status-filter"
            className="administration-offers-page__filter d-flex align-items-center gap-2"
          >
            <Form.Label className="mb-0 text-nowrap">
              {t('statusFilter.label')}
            </Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(event) =>
                handleStatusFilterChange(
                  event.target.value as PlatformAdminOfferStatusFilter
                )
              }
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`statusFilter.${option}`)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-offers-availability-filter"
            className="administration-offers-page__filter d-flex align-items-center gap-2"
          >
            <Form.Label className="mb-0 text-nowrap">
              {t('availabilityFilter.label')}
            </Form.Label>
            <Form.Select
              value={availabilityFilter}
              onChange={(event) =>
                handleAvailabilityFilterChange(
                  event.target.value as PlatformAdminOfferAvailabilityFilter
                )
              }
            >
              {AVAILABILITY_FILTER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`availabilityFilter.${option}`)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-offers-sort"
            className="administration-offers-page__filter administration-offers-page__filter--wide d-flex align-items-center gap-2"
          >
            <Form.Label className="mb-0 text-nowrap">
              {t('sort.label')}
            </Form.Label>
            <Form.Select
              value={sortOption}
              onChange={(event) =>
                handleSortOptionChange(
                  event.target.value as PlatformAdminOfferSortOption
                )
              }
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`sort.${option}`)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-offers-page-size"
            className="administration-offers-page__page-size d-flex align-items-center gap-2"
          >
            <Form.Label className="text-nowrap mb-0">
              {t('pageSize')}
            </Form.Label>
            <Form.Select
              value={pageSize}
              onChange={(event) =>
                handlePageSizeChange(Number(event.target.value))
              }
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {isPending ? (
        <SLRSpinner />
      ) : (
        <>
          <SLRTable<PlatformAdminOffer>
            emptyView={
              <Empty
                labels={
                  t('empty', { returnObjects: true }) as {
                    top: string;
                    bottom: string;
                  }
                }
              />
            }
            sortIcon={getIcon('fas', 'long-arrow-up')}
            columns={columns}
            data={rows}
          />

          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.Prev
                disabled={page <= 0}
                onClick={() =>
                  setOffersFilters((current) => ({
                    ...current,
                    page: Math.max(0, current.page - 1)
                  }))
                }
              />
              {Array.from({ length: totalPages }, (_, index) => (
                <Pagination.Item
                  key={index}
                  active={index === page}
                  onClick={() =>
                    setOffersFilters((current) => ({
                      ...current,
                      page: index
                    }))
                  }
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page >= totalPages - 1}
                onClick={() =>
                  setOffersFilters((current) => ({
                    ...current,
                    page: Math.min(totalPages - 1, current.page + 1)
                  }))
                }
              />
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
};
